import { ILevelCardData } from './LoadCardData';
import CardsHelper from '../../Utility/CardsHelper';
import {
    ICardData,
    DEFAULT_CARD_WIDTH,
    DEFAULT_CARD_HEIGHT,
} from '../../Prototype/Level/Card/ICardData';
import { CardType } from '../../Prototype/Level/Card/CardType';
import { Random } from '../../Utility/Random';
import { LevelHelperSettings } from '../../Constants/Level';
import Environment from '../../Utility/Environment';

/**
 * SOFAs modification of SOLI 1.0 level gereneration logic.
 * This function modifies the data given to it
 * @param outLevelCardData The random cards in `cardsInPlay` will be assigned to a particular value
 * @param cardsHelper
 * @param outCardsInStack The random cards will be assigned to a particular value
 */
export const levelGenerationSOFA2 = (params: {
    outLevelCardData: ILevelCardData;
    cardsHelper: CardsHelper;
    // TODO: merge with the `outLevelCardData` argument
    outCardsInStack: ICardData[];
    matchChance: number;
    isDynamicAILevel: boolean;
    consecutiveUnsuccessfullAttempts: number;
    forceOff: boolean;
    levelNumber: number;
}): ILevelCardData => {
    // Get cards that need random values assigned
    const randomCards = {
        inPlay: params.outLevelCardData.cardsInPlay.filter(
            c => c.type === CardType.value && c.random
        ),
        inStackLength: params.outLevelCardData.cardsInStack.filter(
            v => v === -1
        ).length,
    };

    const cardValueDefinitions = {
        inPlay: [] as number[],
        inStack: [] as number[],
    };

    let lastCardValue: number;
    let newCardOnStack: boolean = true;

    // If there is any card defined on stack use this one as lastCardValue.
    // Otherwise get a random one to start the stack.
    const firstRandomCardIndex: number = params.outCardsInStack.findIndex(
        x => x === undefined
    );
    if (firstRandomCardIndex > 0) {
        lastCardValue = params.outCardsInStack[firstRandomCardIndex - 1].value;
    } else {
        lastCardValue = params.cardsHelper.draw();
        cardValueDefinitions.inStack.push(lastCardValue);
    }

    const debugInfo = {
        matches: 0,
        stackBreaks: 0,
        inPlayBreaks: 0,
    };
    // While there are still cards IN PLAY that need random values
    while (hasRemainingUndefinedCardsInPlay()) {
        if (
            Random.roll(LevelHelperSettings.stackBreakChance) && // Chance to get a random card
            !newCardOnStack && // last card added was NOT a stack card
            hasRemainingUndefinedCardsInStack() // there are cards that still need a value in the STACK
        ) {
            lastCardValue = params.cardsHelper.draw(); // Draw random card
            cardValueDefinitions.inStack.push(lastCardValue); // Add to STACK
            newCardOnStack = true;
            debugInfo.stackBreaks++;
        } else {
            if (
                !params.forceOff &&
                (!params.isDynamicAILevel || Random.roll(params.matchChance))
            ) {
                // Dynamic chance to add a random card to IN PLAY instead of adding a match
                lastCardValue = params.cardsHelper.drawMatch(lastCardValue); // Draw match
                debugInfo.matches++;
            } else {
                lastCardValue = params.cardsHelper.draw(); // Draw random card
                debugInfo.inPlayBreaks++;
            }
            cardValueDefinitions.inPlay.push(lastCardValue); // Add card to IN PLAY
            newCardOnStack = false;
        }
    }
    if (process.env.NODE_ENV === Environment.DEVELOPMENT) {
        logDebugInfo();
    }
    // Filter by "availables" random cards and set its values (in play).
    while (hasRemainingUnassignedInPlayValues()) {
        const selected = getInPlayCardDataFromAvailable();
        selected.value = cardValueDefinitions.inPlay.shift();
    }

    // Fill remaining cards in stack with random values and set stack cards values.
    while (hasRemainingUndefinedCardsInStack()) {
        cardValueDefinitions.inStack.push(params.cardsHelper.draw());
    }

    params.outLevelCardData.cardsInStack.forEach(assignValueToCardInStack);

    return;

    function getInPlayCardDataFromAvailable(): ICardData {
        const availables = availableRandomCardInPlay(randomCards.inPlay);

        let selected: ICardData;
        if (availables.length === 0) {
            // If for some reason there is no more available cards using the old logic, get a random one.
            selected = Random.getRandomItem(randomCards.inPlay);
        } else {
            selected = Random.getRandomItem(availables);
        }
        return selected;
    }
    function assignValueToCardInStack(value: number, index: number) {
        if (value === -1) {
            params.outCardsInStack[index] = {
                type: CardType.value,
                random: true,
                value: cardValueDefinitions.inStack.shift(),
            };
        }
    }
    function hasRemainingUndefinedCardsInPlay(): boolean {
        return randomCards.inPlay.length > cardValueDefinitions.inPlay.length;
    }
    function hasRemainingUndefinedCardsInStack(): boolean {
        return randomCards.inStackLength > cardValueDefinitions.inStack.length;
    }
    function hasRemainingUnassignedInPlayValues(): boolean {
        return cardValueDefinitions.inPlay.length > 0;
    }
    function logDebugInfo(): void {
        console.log([
            `Level ${params.levelNumber}`,
            `Attempt: ${params.consecutiveUnsuccessfullAttempts || 0}`,
            `Is Dynamic: ${params.isDynamicAILevel}`,
            `Match Chance: ${
                params.forceOff
                    ? 'OFF'
                    : params.isDynamicAILevel
                        ? params.matchChance * 100 + '%'
                        : '100%'
            }`,
            `Matches: ${debugInfo.matches}`,
            `Stack Breaks: ${debugInfo.stackBreaks}`,
            `In Play Breaks: ${debugInfo.inPlayBreaks}`,
        ].join('; '));
    }
};

/**
 * This logic was adapted from SOLI 1.0.
 * @param cards
 */
const availableRandomCardInPlay = (cards: ICardData[]): ICardData[] => {
    const halfWidth = DEFAULT_CARD_WIDTH / 2;
    const halfHeight = DEFAULT_CARD_HEIGHT / 2;

    // prettier-ignore
    return cards.filter(card1 => {
        for (const card2 of cards) {
            if (
                (
                    card1.x + halfWidth >= card2.x - halfWidth &&
                    card1.x - halfWidth <= card2.x + halfWidth &&
                    card1.y + halfHeight >= card2.y - halfHeight &&
                    card1.y - halfHeight <= card2.y + halfHeight &&
                    card1.depth < card2.depth &&
                    card2.value === undefined
                ) ||
                    card1.value !== undefined
            ) {
                return false;
            }
        }
        return true;
    });
};
